import React, { type ReactNode, useCallback, useEffect, useMemo } from "react";
import { type DateRange } from "@mui/x-date-pickers-pro";
import { getDateTimeShortcutItems } from "@packsize/components";
import { useToken, useUser } from "@packsize/authentication";
import { DateTime } from "luxon";
import { isEqual } from "lodash";
import { z } from "zod";
import { packsizeHooks } from "@packsize/utils";
import { useIntl } from "react-intl";

import { useTenantTimezone } from "@/hooks/use-tenant-timezone";
import { Loading } from "@/components/loading";
import { useClosingMachineSelection, useMachineSelection } from "@/hooks/reporting";
import type { MachineSelection, MachineSelectionWithChecked } from "@/views/reporting/types/common";
import { machineSelectionWithCheckedSchema } from "@/views/reporting/schemas";
import { sortByValue } from "@/views/reporting/functions/sort";
import { isTierProfessionalOrHigher } from "@/utils/license-tiers";

import { ReportingContext } from "./reporting.context";

interface StoredDateRange {
	dateRange: DateRange<DateTime>;
	shortcut: string | null;
	prevDateRange: DateRange<DateTime> | null;
	prevShortcut: string | null;
}

interface ReportingProviderProps {
	readonly children: ReactNode;
}

export const REPORTING_DATE_TIME_MINUTE_STEP = 15;

const isValidDateRange = (dateRange: DateRange<DateTime>): boolean => {
	const [start, end] = dateRange;

	return start?.isValid === true && end?.isValid === true;
};

const DEFAULT_TIMEZONE = "UTC";

const SELECTED_DATE_RANGE_STORAGE_KEY = "REPORTING_SELECTED_DATE_RANGE_STORAGE_KEY";
const PLANNED_PRODUCTION_STORAGE_KEY = "REPORTING_PLANNED_PRODUCTION_STORAGE_KEY";
const MACHINES_STORAGE_KEY = "REPORTING_MACHINES_STORAGE_KEY";

// Zod schemas for stored data
const storedMachinesSchema = z.object({
	tid: z.string(),
	customerId: z.string(),
	machines: z.array(machineSelectionWithCheckedSchema),
});

const storedPlannedProductionSchema = z.object({
	tid: z.string(),
	enabled: z.boolean(),
});

// DateTime values are serialised as ISO strings by JSON.stringify; parse them back on read.
const nullableDateTimeSchema = z.union([
	z.null(),
	z.string().transform((s) => {
		const value: DateTime<boolean> = DateTime.fromISO(s);

		return value;
	}),
]);

const storedDateRangeSchema = z.object({
	dateRange: z.tuple([nullableDateTimeSchema, nullableDateTimeSchema]),
	shortcut: z.string().nullable(),
	prevDateRange: z.tuple([nullableDateTimeSchema, nullableDateTimeSchema]).nullable(),
	prevShortcut: z.string().nullable(),
});

export const ReportingProvider: React.FC<ReportingProviderProps> = ({ children }) => {
	const intl = useIntl();

	const { currentUser, isPacksizeUser, isPacksizePartner } = useUser();
	const { decodedToken } = useToken();
	const isHydrated = currentUser !== undefined;
	const isMetric = decodedToken?.unitofmeasure === "mm";

	const isPlannedProductionEnabled =
		isTierProfessionalOrHigher(currentUser?.reportingLicenseTier) ||
		isPacksizeUser === true ||
		isPacksizePartner === true;

	const {
		value: storedMachines,
		set: setStoredMachines,
		remove: removeStoredMachines,
	} = packsizeHooks.useTypedLocalStorage(MACHINES_STORAGE_KEY, storedMachinesSchema);

	const machines = useMemo<MachineSelectionWithChecked[] | undefined>(() => {
		const tid = decodedToken?.tid;
		const customerId = decodedToken?.CustomerId;
		if (!storedMachines || !tid || !customerId) return undefined;
		if (storedMachines.tid !== tid || storedMachines.customerId !== customerId) return undefined;
		return storedMachines.machines;
	}, [decodedToken?.CustomerId, decodedToken?.tid, storedMachines]);

	const setMachines = useCallback(
		(newMachines: MachineSelectionWithChecked[] | undefined) => {
			const tid = decodedToken?.tid;
			const customerId = decodedToken?.CustomerId;
			if (newMachines === undefined || !tid || !customerId) {
				removeStoredMachines();
			} else {
				setStoredMachines({ tid, customerId, machines: newMachines });
			}
		},
		[decodedToken?.CustomerId, decodedToken?.tid, removeStoredMachines, setStoredMachines],
	);

	const {
		value: storedPlannedProduction,
		set: setStoredPlannedProduction,
		remove: removeStoredPlannedProduction,
	} = packsizeHooks.useTypedLocalStorage(PLANNED_PRODUCTION_STORAGE_KEY, storedPlannedProductionSchema);

	const usePlannedProduction = useMemo<boolean>(() => {
		const tid = decodedToken?.tid;
		if (!storedPlannedProduction || !tid) return false;
		if (storedPlannedProduction.tid !== tid) return false;
		return storedPlannedProduction.enabled;
	}, [decodedToken?.tid, storedPlannedProduction]);

	const setUsePlannedProduction = useCallback(
		(enabled: boolean | undefined) => {
			const tid = decodedToken?.tid;
			if (enabled === undefined || !tid) {
				removeStoredPlannedProduction();
			} else {
				setStoredPlannedProduction({ tid, enabled });
			}
		},
		[decodedToken?.tid, removeStoredPlannedProduction, setStoredPlannedProduction],
	);

	const { value: storedDateRangeValue, set: setStoredDateRange } = packsizeHooks.useTypedLocalStorage(
		SELECTED_DATE_RANGE_STORAGE_KEY,
		storedDateRangeSchema,
	);
	const storedDateRange: StoredDateRange = storedDateRangeValue ?? {
		dateRange: [DateTime.now().startOf("day"), DateTime.now().endOf("day")],
		shortcut: "today",
		prevDateRange: null,
		prevShortcut: null,
	};

	const { data: tenantTimezone, isLoading: tenantTimezoneLoading } = useTenantTimezone();

	const isTimezoneReady = !tenantTimezoneLoading;

	const timezone = useMemo(() => {
		return tenantTimezone?.siteTimeZone || DEFAULT_TIMEZONE;
	}, [tenantTimezone?.siteTimeZone]);

	const sites = useMemo<string[]>(
		() => (isHydrated ? [decodedToken?.CustomerId ?? "", decodedToken?.tid ?? ""].filter(Boolean) : []),
		[decodedToken?.CustomerId, decodedToken?.tid, isHydrated],
	);

	const setSelectedDateRange = useCallback(
		(
			dateRange: DateRange<DateTime>,
			shortcut: string | null,
			prevDateRange: DateRange<DateTime> | null,
			prevShortcut: string | null,
		) => {
			if (isValidDateRange(dateRange) === false) {
				return;
			}

			if (prevDateRange !== null && isValidDateRange(prevDateRange) === false) {
				return;
			}

			setStoredDateRange({
				dateRange,
				shortcut,
				prevDateRange,
				prevShortcut,
			});
		},
		[setStoredDateRange],
	);

	const shortcuts = useMemo(
		() => getDateTimeShortcutItems(timezone, intl, REPORTING_DATE_TIME_MINUTE_STEP),
		[timezone, intl],
	);
	const selectedShortcut = shortcuts.find((s) => s.id === storedDateRange?.shortcut);

	const rawSelectedDateRange = useMemo<DateRange<DateTime>>(() => {
		const baseRange = selectedShortcut
			? selectedShortcut.getValue({ isValid: () => true })
			: storedDateRange?.dateRange;

		if (!baseRange) return [null, null];

		const [start, end] = baseRange;
		if (!isTimezoneReady) return [start ?? null, end ?? null];

		return [start?.setZone(timezone) ?? null, end?.setZone(timezone) ?? null];
	}, [selectedShortcut, storedDateRange?.dateRange, isTimezoneReady, timezone]);

	const selectedDateRange = rawSelectedDateRange;

	const [startDate, endDate] = selectedDateRange;
	const hasValidDates = startDate?.isValid === true && endDate?.isValid === true;

	const machineQueryParams = useMemo(
		() => ({
			schedules: [],
			sites,
			startDate: startDate ?? DateTime.now().setZone(timezone).startOf("day"),
			endDate: endDate ?? DateTime.now().setZone(timezone).endOf("day"),
			timeZoneId: timezone,
		}),
		[sites, startDate, endDate, timezone],
	);

	const isMachineQueryEnabled = isHydrated && hasValidDates && isTimezoneReady;

	const productionQuery = useMachineSelection(machineQueryParams, {
		enabled: isMachineQueryEnabled,
	});

	const closingQuery = useClosingMachineSelection(machineQueryParams, {
		enabled: isMachineQueryEnabled,
	});

	const fetchedMachines = useMemo(() => {
		if (!productionQuery.isFetched || !closingQuery.isFetched) return undefined;

		const fetchedProductionMachines = productionQuery.data ?? [];
		const fetchedClosingMachines = closingQuery.data ?? [];

		const machineMap = new Map<string, MachineSelection>();

		fetchedProductionMachines.forEach((m) => {
			machineMap.set(m.id, {
				...m,
				isProduction: m.isProduction ?? true,
			});
		});

		fetchedClosingMachines.forEach((m) => {
			const existing = machineMap.get(m.id);
			machineMap.set(m.id, {
				...(existing ?? m),
				...m,
				isProduction: false,
			});
		});

		return Array.from(machineMap.values());
	}, [closingQuery.data, closingQuery.isFetched, productionQuery.data, productionQuery.isFetched]);

	useEffect(() => {
		if (!fetchedMachines) return;

		const currentMachines = machines ?? [];
		const currentMachineMap = new Map(currentMachines.map((m) => [m.id, m]));

		const sortedResultMachines: MachineSelectionWithChecked[] = sortByValue(fetchedMachines, "alias", "asc").map(
			(machine) => {
				const existing = currentMachineMap.get(machine.id);
				return {
					id: machine.id,
					alias: machine.alias,
					...(machine.utcDateTime ? { utcDateTime: machine.utcDateTime } : {}),
					checked: existing ? existing.checked : true,
					isProduction: machine.isProduction ?? true,
				};
			},
		);

		if (!isEqual(sortedResultMachines, machines)) {
			setMachines(sortedResultMachines);
		}
	}, [fetchedMachines, machines, setMachines]);

	const value = useMemo(() => {
		return {
			selectedDateRange,
			selectedShortcut: storedDateRange?.shortcut || null,
			prevDateRange: storedDateRange?.prevDateRange || null,
			prevShortcut: storedDateRange?.prevShortcut || null,
			timezone,
			sites,
			isMetric,

			setSelectedDateRange,

			isPlannedProductionEnabled,
			usePlannedProduction: isPlannedProductionEnabled && Boolean(usePlannedProduction),
			setUsePlannedProduction,

			machines: machines ?? [],
			setMachines,
		};
	}, [
		selectedDateRange,
		storedDateRange?.shortcut,
		storedDateRange?.prevDateRange,
		storedDateRange?.prevShortcut,
		timezone,
		sites,
		isMetric,
		setSelectedDateRange,
		isPlannedProductionEnabled,
		usePlannedProduction,
		setUsePlannedProduction,
		machines,
		setMachines,
	]);

	const isLoading = !isHydrated || !isTimezoneReady;

	return (
		<ReportingContext.Provider value={value}>
			{isLoading ? <Loading /> : null}
			{isLoading === false && children}
		</ReportingContext.Provider>
	);
};
